<template>
    <vx-selectable
        :modelValue="selected"
        placeholder="Select External List"
        :options="options"
        :clearable="true"
        :relativeDropdown="true"
        :disabled="props.disabled"
        :error="props.error"
        @search="search"
        @update:modelValue="emit('update:modelValue', $event)"
    >
        <template v-slot:option="{ option }">
            <div class="flex items-center justify-between gap-2" data-test="external-list-option">
                <span class="truncate" :title="option.name">{{ option.name }}</span>
                <span v-if="option.status === 'disconnected'"
                    class="text-slate-500 shrink-0"
                    data-test="external-list-option-disconnected">
                    <font-awesome-icon :icon="faLinkSlash" class="mr-1"></font-awesome-icon>
                    Disconnected
                </span>
            </div>
        </template>
        <template v-slot:selected-option="{ option }">
            <vx-popover v-if="option.notFound"
                hover
                :arrow="true"
                placement="top">
                <span class="text-slate-500" data-test="external-list-not-found">
                    <font-awesome-icon widthAuto :icon="faGhost" class="mr-1"></font-awesome-icon>
                    Not Found
                </span>
                <template v-slot:content>
                    An external list was used but it couldn't be found.
                </template>
            </vx-popover>
            <div v-else class="flex items-center gap-2" data-test="external-list-selected">
                <span class="truncate" :title="option.name">{{ option.name }}</span>
                <span v-if="option.status === 'disconnected'" class="text-slate-500 shrink-0">
                    <font-awesome-icon :icon="faLinkSlash" class="mr-1"></font-awesome-icon>
                    Disconnected
                </span>
            </div>
        </template>
    </vx-selectable>
</template>

<script setup>
import { VxPopover, VxSelectable } from '@voxie/frontend-components';
import { externalListsClient } from '~/services';
import { computed, onMounted, ref } from 'vue';
import debounce from '~/utils/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faGhost, faLinkSlash } from '@fortawesome/pro-solid-svg-icons';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const teamId = Spark.state.currentTeam.id;
const { currentGroup, currentOrgUnit } = useCurrentGroup();
const context = computed(() => ({ teamId, orgUnitId: currentOrgUnit.value?.id, groupId: currentGroup.value?.group_id }));

const props = defineProps({
    modelValue: {
        type: Object,
        required: false,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    notFound: {
        type: Boolean,
        default: false,
    },
    error: String,
});

const emit = defineEmits(['update:modelValue']);

const options = ref([]);

const selected = computed(() => {
    if (props.modelValue) {
        return { ...props.modelValue, label: props.modelValue.name };
    }

    return props.notFound ? { label: 'Not Found', notFound: true } : null;
});

const search = debounce(async (search, loading) => {
    loading?.(true);

    try {
        const response = await externalListsClient.getExternalLists(context.value, {
            per_page: 10,
            query: search,
        });

        options.value = response.data.data.map((externalContactList) => ({
            ...externalContactList,
            label: externalContactList.name,
        }));
    } finally {
        loading?.(false);
    }
}, 300);

onMounted(() => {
    search();
});
</script>
